// WP-5.3 (M18, S-26 `/explorer`): the first ECharts usage in this app — a thin, generic
// wrapper (init/setOption/resize/dispose + a click passthrough), so the chart config itself
// stays in the page that owns the data. SVG renderer (not canvas): keeps this testable under
// jsdom (no CanvasRenderingContext2D needed) and matches chart spec 18.7's "no 3D, no
// gradients" — SVG is the simpler, lighter-weight choice for a 2D sankey.
import * as echarts from 'echarts';
import { useEffect, useRef } from 'react';

export interface EChartProps {
  option: echarts.EChartsCoreOption;
  onNodeClick?: (name: string) => void;
  height?: number;
  ariaLabel?: string;
}

export function EChart({
  option,
  onNodeClick,
  height = 480,
  ariaLabel,
}: EChartProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (containerRef.current === null) return;
    const chart = echarts.init(containerRef.current, undefined, { renderer: 'svg' });
    chartRef.current = chart;
    const handleResize = (): void => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    // 18.7: "no animation beyond 150ms ease" — capped explicitly, not left at echarts' default.
    chartRef.current?.setOption(
      { animationDuration: 150, animationEasing: 'cubicOut', ...option },
      true,
    );
  }, [option]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart === null || onNodeClick === undefined) return;
    const handler = (params: { name?: string }): void => {
      if (params.name !== undefined) onNodeClick(params.name);
    };
    chart.on('click', handler);
    return () => {
      chart.off('click', handler);
    };
  }, [onNodeClick]);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      style={{ width: '100%', height: `${String(height)}px` }}
    />
  );
}
